// src/utils/CommunityNotificationContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { getAllPosts, getUserLastCommunityVisit } from './firestoreUtils';

const CommunityNotificationContext = createContext();

export const useCommunityNotifications = () => {
  const context = useContext(CommunityNotificationContext);
  if (!context) {
    throw new Error('useCommunityNotifications must be used within a CommunityNotificationProvider');
  }
  return context;
};

export const CommunityNotificationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [hasNewPosts, setHasNewPosts] = useState(false);

  const checkForNewPosts = useCallback(async () => {
    if (!user) {
      setHasNewPosts(false);
      return;
    }

    try {
      const lastVisit = await getUserLastCommunityVisit(user.uid);
      if (!lastVisit) {
        // First time user, no notification
        setHasNewPosts(false);
        return;
      }

      const posts = await getAllPosts();
      const newPosts = posts.filter(post => {
        const postTime = post.timestamp?.toDate();
        return postTime && postTime > lastVisit.toDate();
      });

      setHasNewPosts(newPosts.length > 0);
    } catch (error) {
      console.error('Error checking for new posts:', error);
      setHasNewPosts(false);
    }
  }, [user]);

  useEffect(() => {
    checkForNewPosts();
  }, [checkForNewPosts]);

  // Check periodically for new posts (every 30 seconds)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      checkForNewPosts();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, checkForNewPosts]);

  const clearNotification = () => {
    setHasNewPosts(false);
  };

  return (
    <CommunityNotificationContext.Provider value={{ hasNewPosts, clearNotification, checkForNewPosts }}>
      {children}
    </CommunityNotificationContext.Provider>
  );
};